import { useEffect } from 'react';
import { resolveCockpitKey } from '../utils/cockpitKeys';
import { nextCursor } from '../utils/cursor';

/**
 * Returns true when an editable element (text input, textarea, contenteditable)
 * currently has focus, so cockpit shortcuts can stand down and let the user type.
 */
function isTextInputFocused(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
}

export interface UseCockpitKeyboardParams {
  /** When false, the listener is not attached (e.g. a modal is open). */
  enabled: boolean;
  /** Question ids in their displayed order. */
  orderedIds: string[];
  /** The currently active (focused) question id. */
  activeId: string | null;
  onNavigate: (questionId: string) => void;
  onAccept: (questionId: string) => void;
  onEdit: (questionId: string) => void;
  onReject: (questionId: string) => void;
}

/**
 * Binds the cockpit keyboard model to the window. Arrow/`j`/`k` move the
 * cursor; `a`/`Enter` accept and advance; `e` edits; `r` rejects and advances;
 * `s` skips (advances without an action). The key→intent mapping lives in the
 * pure, tested `resolveCockpitKey`; this hook only wires intents to handlers
 * and manages cursor advancement.
 */
export function useCockpitKeyboard({
  enabled,
  orderedIds,
  activeId,
  onNavigate,
  onAccept,
  onEdit,
  onReject,
}: UseCockpitKeyboardParams): void {
  useEffect(() => {
    if (!enabled) return;

    const move = (direction: 'next' | 'prev') => {
      const target = nextCursor(orderedIds, activeId, direction);
      if (target) onNavigate(target);
    };

    const advance = () => move('next');
    const retreat = () => move('prev');

    const handler = (event: KeyboardEvent) => {
      const intent = resolveCockpitKey({
        key: event.key,
        isTextInputFocused: isTextInputFocused(),
      });
      if (!intent) return;

      // Stop the browser from scrolling on arrows / submitting on Enter.
      event.preventDefault();

      switch (intent) {
        case 'next':
          advance();
          break;
        case 'prev':
          retreat();
          break;
        case 'accept':
          if (activeId) {
            onAccept(activeId);
            advance();
          }
          break;
        case 'edit':
          if (activeId) onEdit(activeId);
          break;
        case 'reject':
          if (activeId) {
            onReject(activeId);
            advance();
          }
          break;
        case 'skip':
          advance();
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, orderedIds, activeId, onNavigate, onAccept, onEdit, onReject]);
}
